import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  DynamicDrawUsage,
  Group,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  ShadowMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import type { FrameSpec } from '../config/frames';
import { unproject, type CameraIntrinsics } from '../pose/CameraIntrinsics';
import type { SolvedFramePose } from '../pose/FramePoseSolver';
import { LANDMARK_COUNT, LM, lmX, lmY, lmZ, type LandmarkBuffer } from '../tracking/landmarks';
import { FACE_MESH_TRIANGLES } from './facemesh/canonicalMesh';

export interface OccluderParams {
  /** Master switch. */
  enabled: boolean;
  /** Face-mesh occluder (468 landmarks, real face shape). */
  faceMesh: boolean;
  /** Head volume (ellipsoid behind the face) + ear blocks. */
  head: boolean;
  ears: boolean;
  /**
   * Pushes the face mesh away from the camera (mm) — a margin so the nose or
   * the cheeks never clip the front of the frame when the fit is a little off.
   */
  faceBiasMm: number;
  /** Lateral margin added to the head ellipsoid beyond the measured head width (mm). */
  headMarginMm: number;
  /** Exponential smoothing of the face-mesh shape in head space (0..1, 1 = raw). */
  meshSmoothing: number;
  /** Strength of the frame's shadow on the face (0 = none). */
  shadowOpacity: number;
}

export const DEFAULT_OCCLUDER_PARAMS: OccluderParams = {
  enabled: true,
  faceMesh: true,
  head: true,
  ears: true,
  faceBiasMm: 2.5,
  headMarginMm: 1.5,
  meshSmoothing: 0.5,
  shadowOpacity: 0.13,
};

/**
 * Head ellipsoid centre and semi-axes relative to the frame origin (mm, head
 * axes). Its front pole stays ~25 mm behind the frame: behind the cheeks and
 * the eyes (the face mesh covers the face itself, and doubles as the shadow
 * catcher, so nothing may sit in front of it), never near the frame front.
 */
const HEAD_CENTRE_Y = -15;
const HEAD_CENTRE_Z = -120;
const HEAD_SEMI_Y = 112;
const HEAD_SEMI_Z = 95;
/**
 * Ear block (pinna): behind the ear-front landmarks, lateral to the temple. The
 * helix rises a few mm above the point where the temple rests, so the block's
 * top sits slightly above the hinge line and hides the shank over the ear and
 * the tip behind it, from the front as from the side.
 */
export const EAR_Z_FRONT = -95;
const EAR_Z_BACK = -178;
const EAR_OUTWARD_MM = 18;
const EAR_INWARD_MM = 10;
const EAR_HEIGHT_MM = 55;
const EAR_TOP_ABOVE_HINGE_MM = 10;
/** Nose tip → ear-front depth over head width, canonical MediaPipe face (100.3 / 153.2). */
const NOSE_TO_EAR_PER_HEAD_WIDTH = 0.655;
/** Landmarks along the side of the head where the temples run (both sides): temple, cheekbone arch, ear front, jaw hinge. */
const TEMPLE_REGION_LANDMARKS = [127, 356, 234, 454, 93, 323, 132, 361, 34, 264, 139, 368, 162, 389, 21, 251, 227, 447, 137, 366, 177, 401, 215, 435, 116, 345, 123, 352, 50, 280] as const;

/**
 * Depth-only geometry that reproduces the wearer's head so the frame is hidden
 * where the head is in front of it: the far temple behind the nose and cheek
 * when the head turns, the temple ends behind the ears, the tips behind the head.
 *
 * Three pieces, all rendered before the frame with colour writes disabled:
 *  - the face mesh: the 468 landmarks unprojected at metric depth, so it has the
 *    real shape and width of the user's face (not an average head);
 *  - a head ellipsoid behind the face, as wide as the measured head, that never
 *    reaches the front of the frame;
 *  - two ear blocks so the temple tips vanish behind the pinnae.
 *
 * The mesh is stored in the frame's local space (raw pose of the same tracker
 * sample) and the whole group is posed with the *smoothed* pose every frame, so
 * occluder and frame move rigidly together — no lag between them.
 */
export class HeadOccluder {
  readonly root = new Group();
  readonly params: OccluderParams = { ...DEFAULT_OCCLUDER_PARAMS };
  private readonly faceMesh: Mesh;
  /** Same geometry, shadow-catcher material: the frame's shadow drawn onto the video. */
  private readonly shadowMesh: Mesh<BufferGeometry, ShadowMaterial>;
  private readonly positions: Float32Array;
  private readonly headSpace = new Group();
  private readonly ellipsoid: Mesh;
  private readonly earL: Mesh;
  private readonly earR: Mesh;
  private hasMesh = false;
  private shadowAllowed = true;
  private depthFactor = 1;
  private lastHeadWidth = NaN;
  private lastFrameId = '';

  private readonly tmpP = new Vector3();
  private readonly invQ = new Quaternion();
  private readonly forward = new Vector3();

  constructor() {
    this.root.name = 'head-occluder';
    const mat = new MeshBasicMaterial({ colorWrite: false, side: DoubleSide });

    const geo = new BufferGeometry();
    this.positions = new Float32Array(LANDMARK_COUNT * 3);
    const attr = new BufferAttribute(this.positions, 3);
    attr.setUsage(DynamicDrawUsage);
    geo.setAttribute('position', attr);
    // Normals are required by the shadow catcher (the shadow normal bias
    // normalises the vertex normal: without the attribute it is NaN and the
    // shadow silently disappears). Recomputed after each landmark update.
    const normals = new BufferAttribute(new Float32Array(LANDMARK_COUNT * 3), 3);
    normals.setUsage(DynamicDrawUsage);
    geo.setAttribute('normal', normals);
    geo.setIndex(new BufferAttribute(FACE_MESH_TRIANGLES, 1));
    this.faceMesh = new Mesh(geo, mat);
    this.faceMesh.name = 'occluder-face';
    this.faceMesh.frustumCulled = false;
    this.faceMesh.renderOrder = -2;
    this.faceMesh.visible = false;
    this.root.add(this.faceMesh);

    // Shadow catcher: same surface, draws only the shadow the frame casts on it.
    const shadowMat = new ShadowMaterial({ opacity: DEFAULT_OCCLUDER_PARAMS.shadowOpacity, depthWrite: false });
    this.shadowMesh = new Mesh(geo, shadowMat);
    this.shadowMesh.name = 'occluder-face-shadow';
    this.shadowMesh.frustumCulled = false;
    this.shadowMesh.receiveShadow = true;
    this.shadowMesh.renderOrder = 0;
    this.shadowMesh.visible = false;
    this.root.add(this.shadowMesh);

    this.headSpace.name = 'occluder-head-space';
    this.root.add(this.headSpace);

    this.ellipsoid = new Mesh(new SphereGeometry(1, 40, 28), mat);
    this.ellipsoid.name = 'occluder-head';
    this.ellipsoid.frustumCulled = false;
    this.ellipsoid.renderOrder = -1;
    this.headSpace.add(this.ellipsoid);

    const earGeo = new BoxGeometry(1, 1, 1);
    this.earL = new Mesh(earGeo, mat);
    this.earL.name = 'occluder-ear-L';
    this.earR = new Mesh(earGeo, mat);
    this.earR.name = 'occluder-ear-R';
    for (const ear of [this.earL, this.earR]) {
      ear.frustumCulled = false;
      ear.renderOrder = -1;
      this.headSpace.add(ear);
    }
  }

  setParams(patch: Partial<OccluderParams>): void {
    Object.assign(this.params, patch);
  }

  /** Renderer-level switch (quality governor) — independent of the user's shadowOpacity setting. */
  setShadowEnabled(enabled: boolean): void {
    this.shadowAllowed = enabled;
  }

  /** Frame-local z of one landmark for a given depth-offset factor (pass 1 of the depth normalisation). */
  private localZ(buf: LandmarkBuffer, k: CameraIntrinsics, solved: SolvedFramePose, i: number, zCanthi: number, mmPerZ: number, factor: number): number {
    const depth = solved.canthiDepthMm + (lmZ(buf, i) - zCanthi) * mmPerZ * factor;
    unproject(k, lmX(buf, i) * k.width, lmY(buf, i) * k.height, depth, this.tmpP);
    return this.tmpP.sub(solved.position).applyQuaternion(this.invQ).z;
  }

  /**
   * Temple opening (deg) needed for the temples to clear the wearer's head
   * along their length: for each landmark of the temple / cheekbone / ear
   * region (frame-local, current smoothed mesh), the angle at which a temple
   * pivoting at the hinge passes `clearanceMm` outside it. Real acetate
   * temples flex outwards on a wide head exactly like this. Null without mesh.
   * @param hingeX |x| of the hinge axis (mm)
   * @param hingeZ z of the hinge axis (mm, negative = behind the front)
   * @param templeCentreX |x| of the temple centre line at the hinge (mm)
   * @param clearanceMm half temple thickness + margin
   */
  requiredTempleSpreadDeg(hingeX: number, hingeY: number, hingeZ: number, templeCentreX: number, clearanceMm: number): number | null {
    if (!this.hasMesh) return null;
    const p = this.positions;
    let best = -Infinity;
    for (const i of TEMPLE_REGION_LANDMARKS) {
      const x = Math.abs(p[i * 3]!);
      const y = p[i * 3 + 1]!;
      const z = p[i * 3 + 2]!;
      const back = hingeZ - z; // mm behind the hinge
      if (back < 8 || back > 130 || Math.abs(y - hingeY) > 22) continue;
      const needed = Math.atan((x + clearanceMm - templeCentreX) / back);
      if (needed > best) best = needed;
    }
    void hingeX;
    return Number.isFinite(best) ? (best * 180) / Math.PI : null;
  }

  /** Correction applied to MediaPipe's depth offsets (1 = as delivered). Debug. */
  get depthCorrection(): number {
    return this.depthFactor;
  }

  /** Depth extent of the current face mesh, nose tip to ear-front midpoint (mm). Debug. */
  get noseToEarMm(): number | null {
    if (!this.hasMesh) return null;
    const p = this.positions;
    const nose = p[LM.NOSE_TIP * 3 + 2]!;
    const ears = (p[LM.RIGHT_EAR * 3 + 2]! + p[LM.LEFT_EAR * 3 + 2]!) / 2;
    return nose - ears;
  }

  /**
   * Rebuilds the face mesh from one tracker sample, in the frame's local space.
   *
   * Landmark depth: MediaPipe's z is a weak-perspective depth in image-width
   * units with an arbitrary origin; it is anchored on the inner canthi, whose
   * metric depth the pose solver knows, and converted with the focal length.
   */
  updateFromLandmarks(buf: LandmarkBuffer, k: CameraIntrinsics, solved: SolvedFramePose): void {
    if (!this.params.enabled || !this.params.faceMesh) return;
    const W = k.width;
    const H = k.height;
    const zScale = W / k.f; // normalised z → fraction of the reference depth
    const zCanthi = (lmZ(buf, LM.RIGHT_EYE_INNER) + lmZ(buf, LM.LEFT_EYE_INNER)) / 2;
    const dRef = solved.canthiDepthMm / Math.max(0.2, 1 + zCanthi * zScale);
    const mmPerZ = zScale * dRef; // mm of depth per unit of normalised z
    const bias = this.params.faceBiasMm;
    this.invQ.copy(solved.quaternion).invert();

    // MediaPipe's z is its least accurate coordinate. Pass 1 measures the depth
    // of the raw mesh (nose tip → ear front, in head space, so it does not
    // depend on the pose) and pass 2 rescales the depth offsets so that this
    // extent matches the anthropometric ratio to the measured head width
    // (canonical face: 100 mm for 153 mm). Bounded, and smoothed over time.
    const target = NOSE_TO_EAR_PER_HEAD_WIDTH * (solved.metrics.headWidthMm ?? 150);
    const rawExtent = this.localZ(buf, k, solved, LM.NOSE_TIP, zCanthi, mmPerZ, 1) - (this.localZ(buf, k, solved, LM.RIGHT_EAR, zCanthi, mmPerZ, 1) + this.localZ(buf, k, solved, LM.LEFT_EAR, zCanthi, mmPerZ, 1)) / 2;
    const wanted = rawExtent > 10 ? clamp(target / rawExtent, 0.6, 1.6) : 1;
    this.depthFactor = this.hasMesh ? this.depthFactor + (wanted - this.depthFactor) * 0.2 : wanted;

    const alpha = this.hasMesh ? clamp01(this.params.meshSmoothing) : 1;
    const pos = this.positions;
    for (let i = 0; i < 468; i++) {
      const depth = solved.canthiDepthMm + (lmZ(buf, i) - zCanthi) * mmPerZ * this.depthFactor + bias;
      unproject(k, lmX(buf, i) * W, lmY(buf, i) * H, depth, this.tmpP);
      this.tmpP.sub(solved.position).applyQuaternion(this.invQ);
      const o = i * 3;
      pos[o] = pos[o]! + (this.tmpP.x - pos[o]!) * alpha;
      pos[o + 1] = pos[o + 1]! + (this.tmpP.y - pos[o + 1]!) * alpha;
      pos[o + 2] = pos[o + 2]! + (this.tmpP.z - pos[o + 2]!) * alpha;
    }
    (this.faceMesh.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    this.faceMesh.geometry.computeVertexNormals();
    this.hasMesh = true;
  }

  /** Forget the smoothed shape (tracking re-acquired on a possibly different face). */
  reset(): void {
    this.hasMesh = false;
    this.depthFactor = 1;
  }

  /**
   * Poses the occluder for one rendered frame.
   * @param position smoothed frame origin (camera space, mm) — null hides everything
   * @param quaternion smoothed frame orientation (includes the pantoscopic tilt)
   * @param spec frame being worn (hinge height, width, tilt)
   * @param headWidthMm measured head width near the ears (null → 150)
   */
  pose(position: Vector3 | null, quaternion: Quaternion | null, spec: FrameSpec, headWidthMm: number | null, frameOpacity = 1): void {
    const p = this.params;
    const show = p.enabled && position !== null && quaternion !== null;
    this.root.visible = show;
    if (!show) return;
    this.root.position.copy(position!);
    this.root.quaternion.copy(quaternion!);
    this.faceMesh.visible = p.faceMesh && this.hasMesh;
    this.shadowMesh.visible = p.faceMesh && this.hasMesh && this.shadowAllowed && p.shadowOpacity > 0.001;
    this.shadowMesh.material.opacity = p.shadowOpacity * frameOpacity;
    this.headSpace.visible = p.head;
    this.earL.visible = p.ears;
    this.earR.visible = p.ears;
    if (!p.head) return;

    // Head parts live in head axes: undo the pantoscopic tilt of the frame.
    const tilt = (spec.fit.pantoscopicTiltDeg * Math.PI) / 180;
    this.headSpace.quaternion.setFromAxisAngle(this.forward.set(1, 0, 0), -tilt);

    const hw = (headWidthMm ?? 150) / 2 + p.headMarginMm;
    if (hw !== this.lastHeadWidth || spec.id !== this.lastFrameId) {
      this.lastHeadWidth = hw;
      this.lastFrameId = spec.id;
      this.ellipsoid.position.set(0, HEAD_CENTRE_Y, HEAD_CENTRE_Z);
      this.ellipsoid.scale.set(hw, HEAD_SEMI_Y, HEAD_SEMI_Z);

      const hingeY = spec.silhouette?.hingeY ?? spec.dims.lensHeightMm / 2 + 3.5 - 4.5;
      const earTop = hingeY + EAR_TOP_ABOVE_HINGE_MM;
      const earCx = hw - p.headMarginMm + (EAR_OUTWARD_MM - EAR_INWARD_MM) / 2;
      const earCy = earTop - EAR_HEIGHT_MM / 2;
      const earCz = (EAR_Z_FRONT + EAR_Z_BACK) / 2;
      const sx = EAR_OUTWARD_MM + EAR_INWARD_MM;
      const sz = EAR_Z_FRONT - EAR_Z_BACK;
      this.earL.position.set(earCx, earCy, earCz);
      this.earL.scale.set(sx, EAR_HEIGHT_MM, sz);
      this.earR.position.set(-earCx, earCy, earCz);
      this.earR.scale.set(sx, EAR_HEIGHT_MM, sz);
    }
  }

  dispose(): void {
    this.faceMesh.geometry.dispose();
    this.ellipsoid.geometry.dispose();
    this.earL.geometry.dispose();
    (this.faceMesh.material as MeshBasicMaterial).dispose();
    this.shadowMesh.material.dispose();
    this.root.removeFromParent();
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
